import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

export interface IUserRegisterBody {
  user: {
    name: string;
    email: string;
    phone: string;
    password: string;
  };
}

interface IUserLoginBody {
  email: string;
  password: string;
}

interface IRequestLoginOtpBody {
  phoneNumber: string;
}

interface IVerifyLoginOtpBody {
  phoneNumber: string;
  otp: string;
  scope: 'store-owner';
}

export interface IOwnerStore {
  _id: string;
  name: { ar: string; en: string };
  logo: { url: string; key: string } | null;
  slug?: string;
  isActive: boolean;
}

export interface ILoginResponseData {
  accessToken: string;
  refreshToken: string;
  expirationTimeInSeconds: number;
  requiresStoreSelection: boolean;
  stores?: IOwnerStore[];
}

export interface IStoreSelectionResponseData {
  accessToken: string;
  refreshToken: string;
  expirationTimeInSeconds: number;
  storeId: string;
  storeName: { ar: string; en: string };
}

export interface IJwtClaims {
  id?: string;
  email?: string;
  phone?: string;
  scope?: string;
  roles?: string[];
  storeId?: string;
  stamp?: string;
  exp?: number;
  iat?: number;
}

export interface IApiResponse<T> {
  statusCode: number;
  message: { en: string; ar: string } | string;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  jwtHelper: JwtHelperService = new JwtHelperService();

  constructor(
    private httpClient: HttpClient,
    private router: Router
  ) {}

  login(userLoginBody: IUserLoginBody): Observable<IApiResponse<ILoginResponseData>> {
    return this.httpClient.post<IApiResponse<ILoginResponseData>>(
      `${environment.apiURL}users/store-owner/login`,
      userLoginBody
    );
  }

  requestLoginOtp(body: IRequestLoginOtpBody): Observable<IApiResponse<{ otpSent?: boolean }>> {
    return this.httpClient.post<IApiResponse<{ otpSent?: boolean }>>(
      `${environment.apiURL}users/store-owner/request-login-otp`,
      body
    );
  }

  verifyLoginOtp(body: IVerifyLoginOtpBody): Observable<IApiResponse<ILoginResponseData>> {
    return this.httpClient.post<IApiResponse<ILoginResponseData>>(
      `${environment.apiURL}users/verify-login-otp`,
      body
    );
  }

  register(userRegisterBody: IUserRegisterBody) {
    return this.httpClient.post(
      `${environment.apiURL}users/store-owner/register`,
      userRegisterBody
    );
  }

  getOwnerStores(): Observable<IApiResponse<IOwnerStore[]>> {
    return this.httpClient.get<IApiResponse<IOwnerStore[]>>(
      `${environment.apiURL}users/store-owner/stores/list`
    );
  }

  selectStore(storeId: string): Observable<IApiResponse<IStoreSelectionResponseData>> {
    return this.httpClient.post<IApiResponse<IStoreSelectionResponseData>>(
      `${environment.apiURL}users/store-owner/stores/select`,
      { storeId }
    );
  }

  persistAuthTokens(data: { accessToken: string; refreshToken: string }) {
    localStorage.setItem('token', data.accessToken);
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    }
  }

  persistSelectedStore(storeId: string, storeName: { ar: string; en: string }) {
    localStorage.setItem('storeId', storeId);
    localStorage.setItem('storeName', JSON.stringify(storeName));
  }

  getSelectedStoreId(): string | null {
    const stored = localStorage.getItem('storeId');
    if (stored) return stored;
    // Single-store logins don't persist a storeId (no store-selection step),
    // so fall back to the storeId baked into the JWT.
    return this.getStoreIdFromToken();
  }

  private getStoreIdFromToken(): string | null {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return null;
    const token = storedToken.startsWith('Bearer ')
      ? storedToken.slice(7)
      : storedToken;
    try {
      const decoded = this.jwtHelper.decodeToken<IJwtClaims>(token);
      return decoded?.storeId ?? null;
    } catch {
      return null;
    }
  }

  getSelectedStoreName(): { ar: string; en: string } | null {
    const raw = localStorage.getItem('storeName');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && ('ar' in parsed || 'en' in parsed)) {
        return { ar: parsed.ar ?? '', en: parsed.en ?? '' };
      }
      return null;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const storedToken = localStorage.getItem('token');

    if (!storedToken) {
      return false;
    }

    const token = storedToken.startsWith('Bearer ')
      ? storedToken.slice(7)
      : storedToken;



    // Block if token is expired
    if (this.jwtHelper.isTokenExpired(token)) return false;

    try {
      const decodedToken = this.jwtHelper.decodeToken<IJwtClaims>(token);
      const scope = decodedToken?.scope;

      const allowedScopes = ['store-owner'];
      if (scope && !allowedScopes.includes(scope)) return false;
    } catch (err) {
      return false;
    }

    return true;
  }


  getAccountMetaData() {
    const accountMetaData = localStorage.getItem('accountMetaData');
    return accountMetaData ? JSON.parse(accountMetaData) : null;
  }

  isAccountCreatedButNotCompleted(): boolean {
    const accountMetaData = this.getAccountMetaData();
    return accountMetaData ? accountMetaData.isAccountCompleted == false : false;
  }

  logout() {
    localStorage.clear();
    window.location.href = '/';
  }
}
